import { Divider, Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { LoadingButton } from 'components';
import { useNotifications } from 'context/NotificationsContext';
import { FormikProps, FormikValues } from 'formik';
import { useRejectJobMutation } from 'graphql/generated';
import { useTranslation } from 'react-i18next';
import React, { useRef, useState } from 'react';
import { RejectJobPostingForm } from './reject-job-posting-form';
import { useStyle } from './reject-job-posting-dialog.style';

type RejectJobPostingDialogProps = {
    jobId: string;
};

export const RejectJobPostingDialog: React.FC<RejectJobPostingDialogProps> = ({ jobId }) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { notify } = useNotifications();

    const initialValues = {
        rejectReason: '',
    };

    const [open, setOpen] = useState(false);

    const handleClickOpen = (): void => {
        setOpen(true);
    };

    const handleClose = (): void => {
        setOpen(false);
    };

    const [rejectJob, { loading }] = useRejectJobMutation({
        onCompleted() {
            notify({
                message: t('jobDetails:rejectedSuccess'),
                type: 'success',
            });
            handleClose();
        },
    });

    const onSubmit = ({ rejectReason }) => {
        rejectJob({
            variables: { job: { jobId, rejectReason } },
        });
    };

    const formRef = useRef<FormikProps<FormikValues>>();

    const submitForm = (): void => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    return (
        <Grid item xs={6}>
            <Button
                className={classes.button}
                fullWidth
                size="large"
                variant="contained"
                color="secondary"
                onClick={handleClickOpen}
            >
                {t('common:reject')}
            </Button>

            <Dialog
                scroll="paper"
                fullWidth
                fullScreen={isMobile}
                open={open}
                onClose={handleClose}
                disableBackdropClick
            >
                <DialogTitle disableTypography>{t('jobDetails:rejectJobPosting')}</DialogTitle>
                <Divider />
                <DialogContent>
                    <RejectJobPostingForm {...{ initialValues, onSubmit, formRef }} />
                </DialogContent>
                <Divider />
                <DialogActions className={classes.actions}>
                    <Button onClick={handleClose} color="primary">
                        {t('common:cancel')}
                    </Button>
                    <LoadingButton
                        color="primary"
                        variant="contained"
                        loading={loading}
                        onClick={submitForm}
                        loadingText={t('common:saving')}
                        text={t('common:save')}
                    />
                </DialogActions>
            </Dialog>
        </Grid>
    );
};
